import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { WellevateByFullscript } from "@shared/assets/logo";
import FullscriptLogoSvg from "@shared/assets/logo/fullscript-logo-green-leaf-pigeon-text.svg";
import { Header } from "@shared/components/Header";
import { useSharedGlobalConfig } from "@shared/hooks";
import { l } from "@shared/locales/i18n";
import { Link } from "@shared/react-router-dom";

import EmersonEcologicsImg from "./emerson-ecologics-by-fullscript.png";

import * as styles from "./AuthHeader.styles";

interface Props {
  route?: string;
  showFromWellevate?: boolean;
  showFromEmersonEcologics?: boolean;
}

const AuthHeader: FC<Props> = ({
  route,
  showFromWellevate = false,
  showFromEmersonEcologics = false,
}) => {
  const { t } = useTranslation();
  const { isFromWellevate, isFromEmersonEcologics, isEmerson } = useSharedGlobalConfig();

  const displayFromWellevate = isFromWellevate && showFromWellevate;
  const displayFromEmersonEcologics = isFromEmersonEcologics && showFromEmersonEcologics;

  const renderWellevateLogo = () => (
    <img
      src={WellevateByFullscript}
      css={[styles.logo.base, styles.logo.wellevate]}
      alt={t(l.companies.Wellevate)}
    />
  );

  const renderEmersonEcologicsLogo = () => (
    <img
      src={EmersonEcologicsImg}
      css={
        displayFromEmersonEcologics
          ? [styles.logo.base, styles.logo.emersonEcologics]
          : styles.emersonLogo
      }
      alt={t(l.companies.EmersonEcologics)}
      data-testid="emerson-logo"
    />
  );

  const renderCompanyByFullscript = () => {
    if (displayFromWellevate) return renderWellevateLogo();
    if (displayFromEmersonEcologics) return renderEmersonEcologicsLogo();

    return null;
  };

  const renderPlatformLogo = () => {
    if (isEmerson && showFromEmersonEcologics) return renderEmersonEcologicsLogo();

    return (
      <>
        <FullscriptLogoSvg css={styles.svgContainer} data-testid="fullscript-logo" />
        {renderCompanyByFullscript()}
      </>
    );
  };

  const renderLogo = () => {
    const content = (
      <div
        css={[
          styles.logoWrapper.base,
          (displayFromWellevate || displayFromEmersonEcologics || isEmerson) &&
            styles.logoWrapper.fromCompanyByFullscript,
        ]}
      >
        {renderPlatformLogo()}
      </div>
    );

    if (route) {
      return (
        <Link aria-label="login" to={route}>
          {content}
        </Link>
      );
    }

    return content;
  };

  return (
    <Header
      css={(displayFromWellevate || displayFromEmersonEcologics || isEmerson) && styles.header}
    >
      {renderLogo()}
    </Header>
  );
};

export { AuthHeader };
